import {useState, useCallback} from 'react';
import {FormSubmitStatus, FormSubmissionOptions} from 'mkt-ssr/lib/forms/types';
import {useFormAugmentation} from 'mkt-ssr/lib/forms/context';
import {useAnalytics} from 'mkt-ssr/lib/analytics';
import {useIsProduction} from 'mkt-ssr/lib/env';

const MARKETO_SANDBOX_FORM_ID = '1001';

interface UseMarketingFormSubmitResult<T> {
  submitForm: (data: T) => Promise<void>;
  status: FormSubmitStatus;
  error: Error | null;
}

export function useMarketingFormSubmit<T extends Record<string, unknown>>(
  options: FormSubmissionOptions,
): UseMarketingFormSubmitResult<T> {
  const [status, setStatus] = useState<FormSubmitStatus>('idle');
  const [error, setError] = useState<Error | null>(null);
  const augmentationData = useFormAugmentation();
  const analytics = useAnalytics();
  const isProduction = useIsProduction();

  const submitForm = useCallback(
    async (data: T) => {
      setStatus('submitting');
      setError(null);

      analytics.action('marketing_form.submit_attempt', {
        category: 'Forms',
        label: `form_${options.formId}`,
      });

      const payload = {
        ...data,
        ...augmentationData,
        formId: options.formId,
      };

      // In non-production environments, use the sandbox form to avoid polluting the marketing database
      if (!isProduction) {
        payload.formId = MARKETO_SANDBOX_FORM_ID;
      }

      try {
        const response = await fetch('/-api/form', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-requested-with': 'mkt',
          },
          body: JSON.stringify(payload),
        });

        if (response.status === 403) {
          setStatus('forbidden');
          analytics.action('marketing_form.submit_forbidden', {
            category: 'Forms',
            label: `form_${options.formId}`,
          });
          const forbiddenError = new Error(
            'This form submission has been blocked. Please contact support if you believe this is an error.',
          );
          setError(forbiddenError);
          options.onError?.(forbiddenError);
          return;
        }

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.internal_tools_message || 'Form submission failed',
          );
        }

        setStatus('success');
        analytics.action('marketing_form.submit_success', {
          category: 'Forms',
          label: `form_${options.formId}`,
        });
        options.onSuccess?.();
      } catch (err) {
        const formError = err instanceof Error ? err : new Error(String(err));
        setStatus('error');
        setError(formError);
        analytics.action('marketing_form.submit_error', {
          category: 'Forms',
          label: `form_${options.formId}`,
        });
        options.onError?.(formError);
      }
    },
    [augmentationData, options, analytics, isProduction],
  );

  return {
    submitForm,
    status,
    error,
  };
}
